/**
 * CDN entry point for @nova/sdk.
 *
 * Loaded via a <script> tag with data attributes:
 *   <script src="https://cdn.nova.../sdk/v1/nova.iife.js"
 *           data-app-id="app_..."
 *           data-key="pk_nova_..."
 *           data-trigger-container="#my-nav"
 *           data-api-url="https://api.nova.example.com">
 *   </script>
 *
 * Provides a command queue so callers can invoke Nova.track() etc.
 * before bootstrap completes, then replays once init() finishes.
 */

import { Nova } from './index.js';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type QueueableMethod =
  | 'identify'
  | 'track'
  | 'openChat'
  | 'closeChat'
  | 'destroy'
  | 'on'
  | 'off';

interface QueuedCall {
  method: QueueableMethod;
  args: unknown[];
}

export interface ScriptAttributes {
  appId: string;
  key: string;
  triggerContainer: string | null;
  apiUrl: string;
}

interface CachedConfig {
  config: RemoteConfig;
  cachedAt: number;
}

interface RemoteConfig {
  chat?: {
    triggerLabel?: string;
    theme?: {
      brandColor?: string;
      backgroundColor?: string;
      textColor?: string;
      fontFamily?: string;
      borderRadius?: string;
    };
  };
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_API_URL = 'https://api.nova.polusailabs.com';
const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes — matches CDN TTL

/* ------------------------------------------------------------------ */
/*  Command Queue                                                      */
/* ------------------------------------------------------------------ */

export interface CommandQueue {
  proxy: Record<QueueableMethod, (...args: unknown[]) => void>;
  pending: QueuedCall[];
  flush: () => void;
}

/**
 * Create a command queue that buffers Nova method calls before bootstrap
 * completes, then replays them once flush() is called.
 */
export function createCommandQueue(): CommandQueue {
  const pending: QueuedCall[] = [];
  let flushed = false;

  const methods: QueueableMethod[] = [
    'identify',
    'track',
    'openChat',
    'closeChat',
    'destroy',
    'on',
    'off',
  ];

  const proxy = {} as Record<QueueableMethod, (...args: unknown[]) => void>;

  for (const method of methods) {
    proxy[method] = (...args: unknown[]) => {
      if (flushed) {
        // After flush, delegate directly to Nova
        const fn = Nova[method] as (...a: unknown[]) => unknown;
        fn(...args);
      } else {
        pending.push({ method, args });
      }
    };
  }

  function flush(): void {
    // Replay all pending calls
    for (const call of pending) {
      const fn = Nova[call.method] as (...a: unknown[]) => unknown;
      fn(...call.args);
    }
    pending.length = 0;
    flushed = true;
  }

  return { proxy, pending, flush };
}

/* ------------------------------------------------------------------ */
/*  Script Attribute Reader                                            */
/* ------------------------------------------------------------------ */

/**
 * Read configuration from the <script> tag's data attributes.
 *
 * @param script - The script element to read from
 * @throws Error if data-app-id or data-key is missing
 */
export function readScriptAttributes(script: HTMLScriptElement): ScriptAttributes {
  const appId = script.getAttribute('data-app-id');
  if (!appId) {
    throw new Error('[Nova CDN] Missing required attribute: data-app-id');
  }

  const key = script.getAttribute('data-key');
  if (!key) {
    throw new Error('[Nova CDN] Missing required attribute: data-key');
  }

  const triggerContainer = script.getAttribute('data-trigger-container') ?? null;
  const apiUrl = script.getAttribute('data-api-url') ?? DEFAULT_API_URL;

  return { appId, key, triggerContainer, apiUrl };
}

/* ------------------------------------------------------------------ */
/*  Config Fetch                                                       */
/* ------------------------------------------------------------------ */

function getCacheKey(appId: string): string {
  return `nova_config_${appId}`;
}

function readCachedConfig(appId: string): CachedConfig | null {
  try {
    const raw = localStorage.getItem(getCacheKey(appId));
    if (!raw) return null;
    return JSON.parse(raw) as CachedConfig;
  } catch {
    return null;
  }
}

function writeCachedConfig(appId: string, config: RemoteConfig): void {
  try {
    const entry: CachedConfig = { config, cachedAt: Date.now() };
    localStorage.setItem(getCacheKey(appId), JSON.stringify(entry));
  } catch {
    // localStorage may be unavailable or full — silently ignore
  }
}

async function computeHmac(key: string, body: string): Promise<string> {
  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(body));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Fetch remote config from the Nova API, with localStorage caching.
 * Falls back to stale cache if the network request fails.
 *
 * @param apiUrl - Base API URL
 * @param appId - Application ID
 * @param key - Publishable key (used for auth headers)
 */
export async function fetchConfig(
  apiUrl: string,
  appId: string,
  key: string,
): Promise<RemoteConfig> {
  const cached = readCachedConfig(appId);

  // Return fresh cache hit
  if (cached && Date.now() - cached.cachedAt < CONFIG_CACHE_TTL_MS) {
    return cached.config;
  }

  try {
    // HMAC signs empty string — same pattern as HttpTransport
    const signature = await computeHmac(key, '');

    const response = await fetch(`${apiUrl}/sdk/v1/config`, {
      method: 'GET',
      headers: {
        'X-Nova-Api-Key': key,
        'X-Nova-Signature': signature,
      },
    });

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }

    const body = (await response.json()) as { data: RemoteConfig };
    const config = body.data;

    writeCachedConfig(appId, config);
    return config;
  } catch {
    // Fall back to stale cache
    if (cached) {
      return cached.config;
    }
    // No cache at all — return empty config
    return {};
  }
}

/* ------------------------------------------------------------------ */
/*  Bootstrap                                                          */
/* ------------------------------------------------------------------ */

/**
 * Bootstrap the Nova SDK from a CDN script tag.
 * Reads data attributes, fetches remote config, initializes the SDK,
 * and flushes the command queue.
 */
export async function bootstrap(): Promise<void> {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    console.warn('[Nova CDN] bootstrap() could not find document.currentScript');
    return;
  }

  const attrs = readScriptAttributes(script);

  const remoteConfig = await fetchConfig(attrs.apiUrl, attrs.appId, attrs.key);

  Nova.init({
    appId: attrs.appId,
    apiUrl: attrs.apiUrl,
    publishableKey: attrs.key,
    // Use 'anonymous' as a sentinel — passes widget validation.
    // Nova.identify() updates to the real user ID once the host app calls it.
    chat: {
      externalUserId: 'anonymous',
      triggerContainer: attrs.triggerContainer ?? undefined,
      triggerLabel: remoteConfig.chat?.triggerLabel,
      theme: remoteConfig.chat?.theme,
    },
  });

  queue.flush();
}

/* ------------------------------------------------------------------ */
/*  Module-level setup                                                 */
/* ------------------------------------------------------------------ */

const queue = createCommandQueue();

// Expose on globalThis.Nova for CDN consumers
const globalNova = {
  init: Nova.init,
  identify: queue.proxy.identify,
  track: queue.proxy.track,
  openChat: queue.proxy.openChat,
  closeChat: queue.proxy.closeChat,
  destroy: queue.proxy.destroy,
  on: queue.proxy.on,
  off: queue.proxy.off,
  flush: Nova.flush,
};

if (typeof globalThis !== 'undefined') {
  (globalThis as Record<string, unknown>).Nova = globalNova;
}

// Auto-bootstrap when loaded via script tag
bootstrap().catch((err) => {
  console.warn('[Nova CDN] Bootstrap failed:', err instanceof Error ? err.message : err);
});
